import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { View } from "react-native";
import { useTranslation } from "react-i18next";
import type { CurrentMax, Draft, UnitsPreference } from "@heraion/shared";

import type { UiTheme } from "../theme";
import { tierOf } from "../useBreakpoint";
import { DayNavigator } from "./DayNavigator";
import { DayView } from "./DayView";
import { ExerciseView } from "./ExerciseView";
import { useRuleWords } from "./ruleWords";
import { useDraftLineOptions } from "./useDraftLineOptions";
import { useV3Navigation, type V3Start } from "./useV3Navigation";
import { makeV3Styles } from "./v3Styles";
import { WideEditorBody } from "./WideEditorBody";

// A program read through the editor's own pieces, nothing written (#358).
// Under 768 the phone's column: the heatmap, then the day or the open
// exercise. Wider, the editor's zones: the day's grid with its four lenses and
// the exercise on the right, from 1100 the structure on the left too. The
// width is the box's own: a card in a two-column list keeps the phone's column.
export function ProgramReadView({
  theme,
  dark,
  draft,
  cellUnit,
  resolveName,
  maxMeta,
  lockedThrough = -1,
  start,
  onPick,
  top,
}: {
  theme: UiTheme;
  dark: boolean;
  draft: Draft;
  cellUnit: UnitsPreference;
  resolveName: (id: string, snapshot?: string) => string;
  /** Absent: no band from a MAX, a row in kilos reads by its sets (#353). */
  maxMeta?: ReadonlyMap<string, CurrentMax>;
  /** Rounds 0..lockedThrough were executed: marked «✓». */
  lockedThrough?: number;
  /** The day and round to open on: the followed card's own pick. */
  start?: V3Start;
  /** The day or round picked here, as it changes: the card's own pick follows. */
  onPick?: (p: V3Start) => void;
  /** What the host shows above the day. */
  top: ReactNode;
}) {
  const { t } = useTranslation();
  const styles = useMemo(() => makeV3Styles(theme), [theme]);
  const [width, setWidth] = useState(0);
  const tier = tierOf(width);
  const nav = useV3Navigation(draft, tier !== "narrow", start);
  // Said once per change, not for the pick it opened on.
  const said = useRef(start);
  useEffect(() => {
    const p = { day: nav.dayIdx, round: nav.roundIdx };
    if (said.current?.day === p.day && said.current.round === p.round) return;
    said.current = p;
    onPick?.(p);
  }, [nav.dayIdx, nav.roundIdx, onPick]);
  const lineOptions = useDraftLineOptions(cellUnit);
  const words = useRuleWords(lineOptions);
  const exerciseView =
    nav.openSlotIdx === null ? null : (
      <ExerciseView
        key={draft.sessions[nav.dayIdx]?.slots[nav.openSlotIdx]?.id}
        theme={theme}
        styles={styles}
        draft={draft}
        nav={nav}
        slotIdx={nav.openSlotIdx}
        lockedThrough={lockedThrough}
        lineOptions={lineOptions}
        cellUnit={cellUnit}
        resolveName={resolveName}
        words={words}
        maxMeta={maxMeta}
        inPane={tier !== "narrow"}
      />
    );
  const navigator = (heatmap: boolean) => (
    <DayNavigator
      theme={theme}
      dark={dark}
      draft={draft}
      nav={nav}
      dayChips={!heatmap}
      heatmap={heatmap}
      maxMeta={maxMeta}
      cellUnit={cellUnit}
      lockedThrough={lockedThrough}
      title={t("mobile.preview.gridTitle", { defaultValue: "Intensity · day × week" })}
    />
  );
  const head = <View style={[styles.gutter, { paddingBottom: theme.spacing.s3 }]}>{top}</View>;

  return (
    <View onLayout={(e) => setWidth(e.nativeEvent.layout.width)}>
      {tier === "narrow" ? (
        <>
          {head}
          {navigator(true)}
          {exerciseView ?? (
            <DayView theme={theme} styles={styles} draft={draft} nav={nav} lineOptions={lineOptions} resolveName={resolveName} />
          )}
        </>
      ) : (
        <WideEditorBody
          theme={theme}
          styles={styles}
          tier={tier}
          draft={draft}
          nav={nav}
          lineOptions={lineOptions}
          cellUnit={cellUnit}
          resolveName={resolveName}
          maxMeta={maxMeta}
          lockedThrough={lockedThrough}
          inline
          top={head}
          navigator={tier === "medium" ? navigator(false) : null}
          panels={null}
          detail={exerciseView}
        />
      )}
    </View>
  );
}
